import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, BookOpen, Loader2 } from 'lucide-react';
import { useAuth } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { kbApi, type KnowledgeBase } from '@/api/kb';
import { useAppConfig } from '@/hooks/useAppConfig';

export default function KnowledgeBaseList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const appConfig = useAppConfig();
  // 單機版免登入，user 為 null（role 因而 undefined），但後端一律以本機
  // super_admin 身分處理——前端比照放行，否則新增/管理按鈕會全被藏起來。
  const singleUserMode = !!appConfig?.single_user_mode;
  const role = (user?.metadata as Record<string, unknown>)?.role as string | undefined;
  const isSuperAdmin = singleUserMode || role === 'super_admin';
  const canManageKb = isSuperAdmin || role === 'org_user';
  const [search, setSearch] = useState('');
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    kbApi
      .list()
      .then(setKbs)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = kbs.filter(
    (kb) =>
      kb.name.toLowerCase().includes(search.toLowerCase()) ||
      kb.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-4">
      {/* 頂部操作列 */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-foreground">知識庫管理</h2>
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜尋名稱或描述"
              className="pl-9"
            />
          </div>
          {canManageKb && (
            <Button
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10 flex-shrink-0"
              onClick={() => navigate('/knowledge-base/create')}
            >
              <Plus className="size-4 mr-1" />
              新增知識庫
            </Button>
          )}
        </div>
      </div>

      {/* 狀態 */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="size-5 animate-spin" />
          <span>載入中…</span>
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-8 text-red-500 text-sm">{error}</div>
      )}

      {/* 知識庫卡片列表 */}
      {!loading && !error && (
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="size-12 mx-auto mb-3 opacity-40" />
              <p>{kbs.length === 0 ? '尚無知識庫，點擊右上角新增' : '找不到符合的知識庫'}</p>
            </div>
          )}
          {filtered.map((kb) => (
            <div
              key={kb.id}
              className="bg-card border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <BookOpen className="size-5 text-primary" />
                    <h3 className="font-semibold text-foreground">{kb.name}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {kb.file_count} 個文件 · 區塊 {kb.chunk_size.toLocaleString()} tokens
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {kb.description || '沒有描述'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => navigate(`/knowledge-base/${kb.id}`)}
                >
                  查看/編輯
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
